import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, Clock, CheckCircle, XCircle, 
  Filter, Search, FileText, ChevronRight, Shield,
  PlusCircle, RefreshCw, ChevronLeft
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getUserDisputes } from '../../services/api';
import Loading from '../../components/common/Loading';
import MobileBottomNav from '../../components/layout/MobileBottomNav';

const DisputeList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (user) {
      fetchDisputes();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchDisputes = async (isRefreshing = false) => {
    try {
      if (!isRefreshing) setLoading(true);
      
      const response = await getUserDisputes(user.user_id);
      
      if (response.success) {
        setDisputes(response.data || []);
      } else {
        // console.error('Failed to load disputes:', response.error);
      }
    } catch (error) {
      // console.error('Error loading disputes:', error);
      alert('Failed to load disputes');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    if (!user) return;
    setRefreshing(true);
    await fetchDisputes(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-4 h-4" /> },
      'in_review': { color: 'bg-blue-100 text-blue-800', icon: <AlertTriangle className="w-4 h-4" /> },
      'resolved': { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-4 h-4" /> },
      'closed': { color: 'bg-gray-100 text-gray-800', icon: <XCircle className="w-4 h-4" /> }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.icon}
        {status.replace('_', ' ')}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const getDisputeTypeBadge = (dispute) => {
    if (dispute.order_id) {
      return (
        <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
          Order #{dispute.order_id}
        </span>
      );
    } else if (dispute.escrow_id) {
      return (
        <span className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
          Escrow #{dispute.escrow_id}
        </span>
      );
    }
    return null;
  };

  const filteredDisputes = disputes.filter(dispute => {
    // Apply status filter
    if (filter !== 'all' && dispute.status !== filter) {
      return false;
    }
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        dispute.dispute_id.toString().includes(searchLower) ||
        (dispute.description && dispute.description.toLowerCase().includes(searchLower)) ||
        (dispute.item_title && dispute.item_title.toLowerCase().includes(searchLower)) ||
        (dispute.raised_by_first_name && dispute.raised_by_first_name.toLowerCase().includes(searchLower)) ||
        (dispute.raised_against_first_name && dispute.raised_against_first_name.toLowerCase().includes(searchLower))
      );
    }
    
    return true;
  });

  // Calculate stats
  const stats = {
    total: disputes.length,
    pending: disputes.filter(d => d.status === 'pending').length,
    in_review: disputes.filter(d => d.status === 'in_review').length,
    resolved: disputes.filter(d => d.status === 'resolved' || d.status === 'closed').length,
    escrow: disputes.filter(d => d.escrow_id).length,
    order: disputes.filter(d => d.order_id).length
  };

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Loading your disputes..." />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => navigate(-1)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h1 className="text-xl font-bold text-gray-900">My Disputes</h1>
              </div>
            </div>
          </div>
        </div>

        {/* Not Logged In State */}
        <div className="max-w-md mx-auto px-4 py-12">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <Shield className="w-12 h-12 text-gray-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Login Required</h2>
            <p className="text-gray-600 mb-8">
              Please login to view and manage your disputes.
            </p>
            
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-orange-600 text-white py-3 rounded-lg font-medium hover:bg-orange-700 transition mb-6"
            >
              Login to Continue
            </button>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-2">What are disputes?</h3>
              <p className="text-gray-600 text-sm">
                Disputes help resolve issues with transactions. When you raise a dispute, 
                funds are frozen and our team helps mediate between both parties.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">My Disputes</h1>
            </div>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Total Disputes</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">In Review</p>
            <p className="text-2xl font-bold text-blue-600">{stats.in_review}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Resolved</p>
            <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl border shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search disputes by ID, description, or name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_review">In Review</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          {/* Type Filters */}
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 rounded-full text-sm ${filter === 'all' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600'}`}
            >
              All ({stats.total})
            </button>
            <button
              onClick={() => {
                // Filter by escrow disputes
                const filtered = disputes.filter(d => d.escrow_id);
                setDisputes(filtered);
                setFilter('all');
              }}
              className={`px-3 py-1 rounded-full text-sm ${stats.escrow > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}
            >
              Escrow ({stats.escrow})
            </button>
            <button
              onClick={() => {
                // Filter by order disputes
                const filtered = disputes.filter(d => d.order_id);
                setDisputes(filtered);
                setFilter('all');
              }}
              className={`px-3 py-1 rounded-full text-sm ${stats.order > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
            >
              Order ({stats.order})
            </button>
          </div>
        </div>

        {/* Disputes List */}
        {filteredDisputes.length === 0 ? (
          <div className="bg-white rounded-xl border shadow-sm p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Disputes Found</h3>
            <p className="text-gray-600 mb-6">
              {search || filter !== 'all' 
                ? 'No disputes match your search criteria'
                : 'You have no disputes at the moment'}
            </p>
            {!search && filter === 'all' && (
              <div className="bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
                <h4 className="font-medium text-gray-900 mb-2">How disputes work:</h4>
                <ul className="text-sm text-gray-600 text-left space-y-2">
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Raise a dispute when there's an issue with a transaction</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Funds are frozen until the dispute is resolved</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Upload evidence to support your case</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Our support team mediates between both parties</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDisputes.map((dispute) => (
              <div 
                key={dispute.dispute_id}
                onClick={() => navigate(`/disputes/${dispute.dispute_id}`)}
                className="bg-white rounded-xl border shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">
                        Dispute #{dispute.dispute_id}
                      </h3>
                      {getStatusBadge(dispute.status)}
                      {getDisputeTypeBadge(dispute)}
                    </div>
                    
                    <p className="text-gray-700 line-clamp-2 mb-3">
                      {dispute.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{formatPrice(dispute.amount)}</span>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="capitalize">{dispute.category?.replace(/_/g, ' ')}</span>
                      </div>
                      
                      {dispute.item_title && (
                        <div className="flex items-center gap-1">
                          <span>Item: {dispute.item_title}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-1">
                        <span>Created: {formatDate(dispute.created_at)}</span>
                      </div>
                      
                      {dispute.evidence_count > 0 && (
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span>{dispute.evidence_count} evidence</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Parties Involved */}
                    <div className="flex flex-wrap gap-4 mt-3 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">Raised by:</span>
                        <span className="font-medium">
                          {dispute.raised_by_first_name} {dispute.raised_by_last_name}
                          {user.user_id === dispute.raised_by_user_id && (
                            <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              You
                            </span>
                          )}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">Against:</span>
                        <span className="font-medium">
                          {dispute.raised_against_first_name} {dispute.raised_against_last_name}
                          {user.user_id === dispute.raised_against_user_id && (
                            <span className="ml-2 bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                              You
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-gray-400 ml-4 mt-2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {filteredDisputes.length > 0 && (
          <div className="mt-8 bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-600">
              Showing {filteredDisputes.length} of {disputes.length} disputes
              {filter !== 'all' && ` (filtered by ${filter})`}
              {search && ` (matching "${search}")`}
            </p>
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default DisputeList;